import type { DirectoryKind, DirectoryPerson } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { peopleDirectoryQuery } from '@/lib/queries'

/**
 * The Directory tab — "who can reach what", across every account at once.
 *
 * Separate from Users because it answers a different question. Users is where somebody is invited
 * and given a platform role; this is the read: show me every external admin, or everyone on TD
 * Bank, without opening each account in turn.
 *
 * Filtering is client-side on purpose. The dataset is the access list itself — one row per grant,
 * not per event — so it arrives whole, and a server round trip per keystroke would buy nothing
 * except a cache entry per keystroke, which is the trap CLAUDE.md warns about for query keys.
 */
export const Route = createFileRoute('/app/access/directory')({
  loader: ({ context }) => context.queryClient.ensureQueryData(peopleDirectoryQuery),
  component: DirectoryPage,
})

const KINDS: { value: DirectoryKind | 'all'; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'pending', label: 'Pending' },
]

/** A person matches a resource or capability filter when ANY of their entries does. */
function matches(person: DirectoryPerson, resourceId: string, capability: string): boolean {
  if (resourceId === 'all' && capability === 'all') return true
  return person.entries.some((entry) => entryMatches(entry, resourceId, capability))
}

function DirectoryPage() {
  const { data } = useSuspenseQuery(peopleDirectoryQuery)
  const [kind, setKind] = useState<DirectoryKind | 'all'>('all')
  const [resourceId, setResourceId] = useState('all')
  const [capability, setCapability] = useState('all')

  // Options come from what is actually held, not from a catalogue: an account nobody has access
  // to is not a useful thing to filter by, and a capability nobody holds is noise.
  const { resources, capabilities } = useMemo(() => {
    const byId = new Map<string, string>()
    const caps = new Set<string>()
    for (const person of data.people) {
      for (const entry of person.entries) {
        byId.set(entry.resourceId, entry.resourceName)
        caps.add(entry.capability)
      }
    }
    return {
      resources: [...byId].sort((a, b) => a[1].localeCompare(b[1])),
      capabilities: [...caps].sort(),
    }
  }, [data.people])

  const shown = data.people.filter(
    (person) => (kind === 'all' || person.kind === kind) && matches(person, resourceId, capability),
  )

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold">People</h2>
        <p className="text-sm text-muted-foreground">
          Everyone who can reach anything, internal or not. Access shown here comes from the local
          mirror of WorkOS
          {data.reconciledAt
            ? `, last reconciled ${new Date(data.reconciledAt).toLocaleString()}.`
            : ' — which has not been reconciled yet, so it shows only grants made through this app.'}
        </p>
      </div>

      <DirectoryFilters
        kind={kind}
        onKind={setKind}
        resourceId={resourceId}
        onResourceId={setResourceId}
        capability={capability}
        onCapability={setCapability}
        resources={resources}
        capabilities={capabilities}
        shownCount={shown.length}
        totalCount={data.people.length}
      />

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Person</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">Where</th>
                  <th className="px-4 py-2 font-medium">Capability</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                      Nobody matches those filters.
                    </td>
                  </tr>
                )}
                {shown.map((person) => (
                  <PersonRows
                    key={person.membershipId ?? person.email}
                    person={person}
                    resourceId={resourceId}
                    capability={capability}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

interface DirectoryFiltersProps {
  kind: DirectoryKind | 'all'
  onKind: (kind: DirectoryKind | 'all') => void
  resourceId: string
  onResourceId: (resourceId: string) => void
  capability: string
  onCapability: (capability: string) => void
  resources: [string, string][]
  capabilities: string[]
  shownCount: number
  totalCount: number
}

function DirectoryFilters({
  kind,
  onKind,
  resourceId,
  onResourceId,
  capability,
  onCapability,
  resources,
  capabilities,
  shownCount,
  totalCount,
}: DirectoryFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex gap-1" role="group" aria-label="Filter by kind">
        {KINDS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={kind === option.value}
            onClick={() => onKind(option.value)}
            className={
              kind === option.value
                ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                : 'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
            }
          >
            {option.label}
          </button>
        ))}
      </div>

      <select
        value={resourceId}
        onChange={(event) => onResourceId(event.target.value)}
        aria-label="Filter by account"
        className="h-8 rounded-md border border-border bg-background px-2 text-sm"
      >
        <option value="all">Any account</option>
        {resources.map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </select>

      <select
        value={capability}
        onChange={(event) => onCapability(event.target.value)}
        aria-label="Filter by capability"
        className="h-8 rounded-md border border-border bg-background px-2 text-sm"
      >
        <option value="all">Any capability</option>
        {capabilities.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>

      <span className="ml-auto text-xs text-muted-foreground tabular-nums">
        {shownCount} of {totalCount}
      </span>
    </div>
  )
}

/** An entry matches when it passes both the account and the capability filter. */
function entryMatches(
  entry: DirectoryPerson['entries'][number],
  resourceId: string,
  capability: string,
): boolean {
  return (
    (resourceId === 'all' || entry.resourceId === resourceId) &&
    (capability === 'all' || entry.capability === capability)
  )
}

/** One person's rows: one per matching entry, or a single placeholder row when they hold none. */
function PersonRows({
  person,
  resourceId,
  capability,
}: {
  person: DirectoryPerson
  resourceId: string
  capability: string
}) {
  const rows =
    person.entries.length > 0
      ? person.entries.filter((entry) => entryMatches(entry, resourceId, capability))
      : [null]
  return rows.map((entry, index) => (
    <tr
      key={`${person.membershipId ?? person.email}-${entry?.resourceId ?? 'none'}-${index}`}
      className="border-b border-border last:border-0"
    >
      {/* The person's own cell spans their entries — repeating a name down five
          rows reads as five people. */}
      <td className="px-4 py-2 align-top">
        {index === 0 && (
          <>
            <div className="font-medium">{person.name ?? person.email}</div>
            {person.name && <div className="text-xs text-muted-foreground">{person.email}</div>}
          </>
        )}
      </td>
      <td className="px-4 py-2 align-top">
        {index === 0 && (
          <Badge variant="outline" className="rounded-full text-xs">
            {person.kind === 'internal' ? (person.role ?? 'internal') : person.kind}
          </Badge>
        )}
      </td>
      <td className="px-4 py-2 align-top">
        {entry ? (
          entry.resourceName
        ) : (
          <span className="text-muted-foreground">
            {person.kind === 'internal' ? 'Every account' : 'No access yet'}
          </span>
        )}
      </td>
      <td className="px-4 py-2 align-top">{entry?.capability ?? '—'}</td>
      <td className="px-4 py-2 align-top text-xs text-muted-foreground">
        <EntrySource entry={entry} kind={person.kind} />
      </td>
    </tr>
  ))
}

function EntrySource({
  entry,
  kind,
}: {
  entry: DirectoryPerson['entries'][number] | null
  kind: DirectoryPerson['kind']
}) {
  if (!entry) return kind === 'internal' ? 'org-wide role' : '—'
  return (
    <>
      {entry.source}
      {entry.expiresAt && (
        <span className="ml-1 text-warning">
          · expires {new Date(entry.expiresAt).toLocaleDateString()}
        </span>
      )}
    </>
  )
}
